import { useMutation, useQuery } from "@tanstack/react-query";

import { adminKnowledgeApi } from "@/lib/api";
import type {
  KennisItemAntwoord,
  KennisOpstelhulpVoorstel,
} from "@/types/api";

export interface KennisFilters {
  zoek?: string;
  categorie?: string;
  actief?: boolean;
}

export const knowledgeKeys = {
  all: ["admin-knowledge"] as const,
  lijst: (filters?: KennisFilters) =>
    [
      "admin-knowledge",
      "lijst",
      filters?.zoek ?? "",
      filters?.categorie ?? "",
      filters?.actief ?? "alle",
    ] as const,
  detail: (id: string) => ["admin-knowledge", "detail", id] as const,
  categorieen: ["admin-knowledge", "categorieen"] as const,
};

/** Lijst van kennis-items (admin-only). Filtert op vrije tekst, categorie en actief-status. */
export function useKennisItems(filters?: KennisFilters) {
  return useQuery<KennisItemAntwoord[]>({
    queryKey: knowledgeKeys.lijst(filters),
    queryFn: () => adminKnowledgeApi.lijst(filters),
    staleTime: 30_000,
  });
}

/** Eén kennis-item (voor edit-form). */
export function useKennisItem(id: string | undefined) {
  return useQuery<KennisItemAntwoord>({
    queryKey: knowledgeKeys.detail(id ?? ""),
    queryFn: () => adminKnowledgeApi.detail(id!),
    enabled: Boolean(id),
  });
}

/** Beschikbare categorieën voor de filter-dropdown. */
export function useKennisCategorieen() {
  return useQuery<string[]>({
    queryKey: knowledgeKeys.categorieen,
    queryFn: () => adminKnowledgeApi.categorieen(),
    staleTime: 60_000,
  });
}

/** AI-opstelhulp: ruwe tekst → gestructureerd formuliervoorstel. */
export function useKennisOpstelhulp() {
  return useMutation<KennisOpstelhulpVoorstel, unknown, string>({
    mutationFn: (ruweTekst: string) => adminKnowledgeApi.opstelhulp(ruweTekst),
    // De opstelhulp toont een eigen melding op mutate-niveau.
    meta: { eigenFoutmelding: true },
  });
}
